import React from 'react';
import { useStore } from 'reactflow';
import { ClusterBounds } from '../utils/layoutEngine.types';

interface ClusterBackgroundProps {
  clusters: ClusterBounds[];
}

/**
 * Rendert visuele cluster-achtergronden achter de React Flow nodes.
 * Pant en zoomt mee dankzij de viewport-transform uit de React Flow store.
 *
 * Sortering: grotere clusters eerst (renderen onderaan in de stacking order)
 * zodat kleine clusters niet door grotere worden afgedekt.
 */
const ClusterBackground: React.FC<ClusterBackgroundProps> = ({ clusters }) => {
  const transform = useStore((s) => s.transform);
  if (clusters.length === 0) return null;

  // Render grootste clusters eerst (onderaan), zodat kleine clusters bovenop
  // zichtbaar blijven. Externals altijd bovenaan in de stack.
  const sorted = [...clusters].sort((a, b) => {
    if (a.id === 'external') return 1;
    if (b.id === 'external') return -1;
    return b.width * b.height - a.width * a.height;
  });

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      <g
        transform={`translate(${transform[0]}, ${transform[1]}) scale(${transform[2]})`}
      >
        {sorted.map((cluster) => {
          const isExternal = cluster.id === 'external';
          const labelPaddingX = 14;
          const labelPaddingY = 6;
          const labelFontSize = 14;
          const charWidth = 7.5; // approximation
          const labelWidth = cluster.label.length * charWidth + labelPaddingX * 2;
          const labelHeight = labelFontSize + labelPaddingY * 2;

          return (
            <g key={cluster.id}>
              {/* Cluster background rect */}
              <rect
                x={cluster.x}
                y={cluster.y}
                width={cluster.width}
                height={cluster.height}
                fill={cluster.color}
                stroke={cluster.borderColor}
                strokeWidth={2}
                strokeDasharray={isExternal ? '6 4' : undefined}
                rx={12}
                ry={12}
                opacity={0.85}
              />
              {/* Label pill — top-left, slightly above the rect for clear separation */}
              <rect
                x={cluster.x + 12}
                y={cluster.y - labelHeight / 2}
                width={Math.min(labelWidth, cluster.width - 24)}
                height={labelHeight}
                fill="#FFFFFF"
                stroke={cluster.borderColor}
                strokeWidth={1.5}
                rx={labelHeight / 2}
                ry={labelHeight / 2}
              />
              <text
                x={cluster.x + 12 + labelPaddingX}
                y={cluster.y + labelPaddingY / 2 + 1}
                fontFamily="Inter, system-ui, sans-serif"
                fontSize={labelFontSize}
                fontWeight={600}
                fill="#1F2937"
              >
                {cluster.label}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default ClusterBackground;
